import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery, useTheme, Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import BottomMenu from './common/components/BottomMenu';
import TopBar, { TOPBAR_HEIGHT } from './common/components/TopBar';
import PremiumSidebar from './common/components/PremiumSidebar';
import SocketController from './SocketController';
import CachingController from './CachingController';
import { useCatch, useEffectAsync } from './reactHelper';
import { sessionActions } from './store';
import UpdateController from './UpdateController';
import TermsDialog from './common/components/TermsDialog';
import Loader from './common/components/Loader';
import fetchOrThrow from './common/util/fetchOrThrow';
import { WidgetDockProvider } from './common/components/WidgetDockContext';
import { usePremiumTokens } from './common/theme/usePremiumTokens';
// Keyboard Shortcuts
import { KeyboardShortcutsProvider, useKeyboardShortcutsContext } from './common/components/KeyboardShortcutsProvider';
import {
  KeyboardShortcutsHelp,
  PremiumCommandBar,
  ToastProviderWithGlobal,
  ModalProvider,
} from './common/components/premium';
// GlitchTip: importa apenas para usar setContext (inicialização está em instrument.js)
import { Sentry } from './instrument';

// BottomMenu height constant
const BOTTOM_MENU_HEIGHT = 60;

// Componente wrapper para CommandBar que usa o contexto
const ConnectedCommandBar = () => {
  const { commandBarOpen, setCommandBarOpen } = useKeyboardShortcutsContext();

  // Memoize selectors to avoid unnecessary rerenders
  const devicesItems = useSelector((state) => state.devices.items);
  const driversItems = useSelector((state) => state.drivers.items);
  const geofencesItems = useSelector((state) => state.geofences.items);

  const devices = useMemo(() => Object.values(devicesItems), [devicesItems]);
  const drivers = useMemo(() => Object.values(driversItems), [driversItems]);
  const geofences = useMemo(() => Object.values(geofencesItems), [geofencesItems]);

  return (
    <PremiumCommandBar
      open={commandBarOpen}
      onClose={() => setCommandBarOpen(false)}
      vehicles={devices}
      drivers={drivers}
      geofences={geofences}
    />
  );
};

const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
  },
  container: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  page: {
    flexGrow: 1,
    overflow: 'auto',
    height: '100%',
  },
  menu: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1200,
    '@media print': {
      display: 'none',
    },
  },
}));

const App = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const tokens = usePremiumTokens();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  // Safe token access with fallbacks
  const safeTokens = {
    surface: {
      base: tokens?.surface?.base || '#0d1117',
    },
  };

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const newServer = useSelector((state) => state.session.server.newServer);
  const termsUrl = useSelector((state) => state.session.server.attributes.termsUrl);
  const user = useSelector((state) => state.session.user);

  // Check if we should show sidebar
  // NÃO mostrar em: páginas que têm menu próprio (SettingsMenu, ReportsMenu, ManagementMenu)
  // ou páginas fullscreen com mapa (geofences, replay, etc.)
  const hasOwnMenu = pathname.startsWith('/reports')
    || pathname.startsWith('/replay')
    || pathname.startsWith('/heatmap')
    || pathname.startsWith('/eventmap')
    || pathname.startsWith('/geofences')
    || pathname.startsWith('/settings')
    || pathname.startsWith('/fleet')
    || pathname.startsWith('/temperature')
    || pathname.startsWith('/management')
    || pathname.startsWith('/configurator')
    || pathname.startsWith('/assistant');
  const showSidebar = desktop && !hasOwnMenu && pathname !== '/';

  const acceptTerms = useCatch(async () => {
    const response = await fetchOrThrow(`/api/users/${user.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...user, attributes: { ...user.attributes, termsAccepted: true } }),
    });
    dispatch(sessionActions.updateUser(await response.json()));
  });

  useEffectAsync(async () => {
    if (!user) {
      const response = await fetch('/api/session');
      if (response.ok) {
        const user = await response.json()
        dispatch(sessionActions.updateUser(user));
        Sentry.setContext('user', user)
      } else {
        window.sessionStorage.setItem('postLogin', pathname + search);
        navigate(newServer ? '/register' : '/login', { replace: true });
      }
    }
    return null;
  }, []);

  if (user == null) {
    return (<Loader />);
  }
  if (termsUrl && !user.attributes.termsAccepted) {
    return (
      <TermsDialog
        open
        onCancel={() => navigate('/login')}
        onAccept={() => acceptTerms()}
      />
    );
  }
  return (
    <KeyboardShortcutsProvider>
      <ModalProvider>
        <WidgetDockProvider>
        <Box className={classes.root}>
          <SocketController />
          <CachingController />
          <UpdateController />
          <TopBar />
          <Box
            className={classes.container}
            sx={{
              marginTop: `${TOPBAR_HEIGHT}px`,
              height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
            }}
          >
            {/* Premium Sidebar - Desktop only, hidden on map */}
            {showSidebar && <PremiumSidebar />}

            {/* Main Content */}
            <Box
              className={classes.page}
              sx={{
                backgroundColor: safeTokens.surface.base,
                // Padding bottom for mobile BottomMenu
                pb: !desktop ? `${BOTTOM_MENU_HEIGHT}px` : 0,
              }}
            >
              <Outlet />
            </Box>
          </Box>
          {/* Bottom Menu - Mobile only */}
          {!desktop && (
            <div className={classes.menu}>
              <BottomMenu />
            </div>
          )}
        </Box>
        {/* Global Keyboard Shortcuts Help Modal */}
        <KeyboardShortcutsHelp />
        {/* Global Command Bar (⌘K) */}
        <ConnectedCommandBar />
        </WidgetDockProvider>
        {/* Global Toast Notifications */}
        <ToastProviderWithGlobal position="bottom-right" maxToasts={5} />
      </ModalProvider>
    </KeyboardShortcutsProvider>
  );
};

export default App;
